import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ui/use-toast';
import { getPwdRules } from '../utils/passwordRules';

function ChangePasswordModal({ open, onOpenChange }) {
  const { changePassword } = useAuth();
  const { toast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setIsSubmitting(false);
    }
  }, [open]);

  const rules = getPwdRules(newPassword, currentPassword);
  const allMet = rules.every(r => r.met);
  const showChecklist = newPassword.length > 0;
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) { setError('Please enter your current password.'); return; }
    if (rules.some(r => !r.met)) { setError('Please meet all password requirements.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }

    setIsSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast({ title: 'Password changed', description: 'Your password has been updated successfully.' });
      onOpenChange(false);
    } catch (err) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-semibold">Change password</DialogTitle>
          <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {error && (
              <div className="text-body-sm text-red-critical bg-red-50 border border-red-200 rounded-card px-3 py-2">
                {error}
              </div>
            )}

            <div>
              <label className="text-body-sm font-medium text-text-dark mb-1.5 block">Current password</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => { setError(''); setCurrentPassword(e.target.value); }}
                autoComplete="current-password"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="text-body-sm font-medium text-text-dark mb-1.5 block">New password</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => { setError(''); setNewPassword(e.target.value); }}
                autoComplete="new-password"
                placeholder="At least 12 characters"
              />
            </div>

            {showChecklist && (
              <div className={`rounded-card border px-3 py-2.5 space-y-0.5 transition-colors ${allMet ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-nn-border'}`}>
                {rules.map((rule) => (
                  <div key={rule.label} className={`flex items-center gap-2 text-caption transition-colors ${rule.met ? 'text-green-600' : 'text-text-muted'}`}>
                    {rule.met
                      ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><circle cx="12" cy="12" r="9"/></svg>
                    }
                    <span>{rule.label}</span>
                  </div>
                ))}
              </div>
            )}

            <div>
              <label className="text-body-sm font-medium text-text-dark mb-1.5 block">Confirm new password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => { setError(''); setConfirmPassword(e.target.value); }}
                autoComplete="new-password"
                placeholder="Repeat new password"
                className={mismatch ? 'border-red-300 focus-visible:ring-red-300' : ''}
              />
              {mismatch && (
                <p className="text-caption text-red-critical mt-1">Passwords do not match.</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button variant="save" type="submit" disabled={isSubmitting || !allMet}>
              {isSubmitting ? 'Changing\u2026' : 'Change password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ChangePasswordModal;
